import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { Button, FieldError, HelperText, Input, Label } from '@/components/ui';
import { AuthShell } from '@/components/AuthShell';
import { partnerApi } from '@/lib/api-affiliate';

const schema = z
  .object({
    email: z.string().email('Некорректный email'),
    password: z.string().min(8, 'Минимум 8 символов'),
    passwordConfirm: z.string(),
    telegramContact: z.string().optional(),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Пароли не совпадают',
  });
type FormValues = z.infer<typeof schema>;

export function RegisterPage() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const register = partnerApi.auth.useRegister();

  const { register: rhf, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    const { passwordConfirm: _pc, ...payload } = values;
    void _pc;
    await register.mutateAsync(payload);
    setSubmittedEmail(values.email);
  };

  if (submittedEmail) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--success-soft)] grid place-items-center text-[var(--success)] mb-4">
            <CheckCircle2 size={26} />
          </div>
          <h1 className="text-[22px] font-semibold mb-2 text-[var(--text)] tracking-tight">
            Заявка отправлена
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed">
            Спасибо! Мы напишем на{' '}
            <b className="text-[var(--text)]">{submittedEmail}</b> сразу после одобрения — обычно в течение 24 часов.
          </p>
          <Link to="/login" className="block w-full">
            <Button variant="outline" className="w-full">
              Вернуться к входу
            </Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Регистрация партнёра"
      subtitle="Заполните анкету — обычно одобряем в течение 24 часов"
      footer={
        <>
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-[var(--accent-bright)] hover:underline font-medium">
            Войти
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...rhf('email')} />
          <FieldError message={formState.errors.email?.message} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="password">Пароль</Label>
            <Input id="password" type="password" autoComplete="new-password" {...rhf('password')} />
            <FieldError message={formState.errors.password?.message} />
          </div>
          <div>
            <Label htmlFor="passwordConfirm">Повторите</Label>
            <Input
              id="passwordConfirm"
              type="password"
              autoComplete="new-password"
              {...rhf('passwordConfirm')}
            />
            <FieldError message={formState.errors.passwordConfirm?.message} />
          </div>
        </div>

        <div>
          <Label htmlFor="telegramContact">Telegram</Label>
          <Input id="telegramContact" placeholder="@username" {...rhf('telegramContact')} />
          <HelperText>Опционально — для быстрой связи</HelperText>
        </div>

        <Button
          type="submit"
          loading={register.isPending}
          className="w-full"
          variant="gradient"
          size="lg"
        >
          {register.isPending ? 'Отправка…' : 'Подать заявку'}
        </Button>
      </form>
    </AuthShell>
  );
}
